import React from 'react';
import styled, { keyframes } from 'styled-components';
import { MdInbox, MdPeopleOutline, MdReceiptLong, MdNotificationsNone, MdBarChart } from 'react-icons/md';

// ── Shimmer animation ────────────────────────────────────────────────────────

const shimmer = keyframes`
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
`;

const SkeletonBase = styled.div`
  background: linear-gradient(
    90deg,
    ${p => p.theme.colors.backgroundDark} 25%,
    ${p => p.theme.colors.border}         50%,
    ${p => p.theme.colors.backgroundDark} 75%
  );
  background-size: 1200px 100%;
  animation: ${shimmer} 1.5s infinite linear;
  border-radius: ${p => p.r || p.theme.borderRadius.sm};
`;

export const SkeletonLine = styled(SkeletonBase)`
  height: ${p => p.h || '13px'};
  width:  ${p => p.w || '100%'};
`;

export const SkeletonCircle = styled(SkeletonBase)`
  width:  ${p => p.size || '36px'};
  height: ${p => p.size || '36px'};
  border-radius: 50%;
  flex-shrink: 0;
`;

export const SkeletonBlock = styled(SkeletonBase)`
  height: ${p => p.h || '80px'};
  width:  ${p => p.w || '100%'};
  border-radius: ${p => p.r || p.theme.borderRadius.lg};
`;

// ── Table skeleton ───────────────────────────────────────────────────────────

const SkRow = styled.tr`
  td { padding: 14px 12px; border-bottom: 1px solid ${p => p.theme.colors.borderLight}; }
`;

const Cell = styled.td``;

export const SkeletonTableRows = ({ rows = 6, cols = 6 }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <SkRow key={i}>
        {/* first col: avatar + name */}
        <Cell>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <SkeletonCircle size="32px" />
            <SkeletonLine h="13px" w="130px" />
          </div>
        </Cell>
        {Array.from({ length: cols - 2 }).map((_, j) => (
          <Cell key={j}>
            <SkeletonLine h="13px" w={`${60 + Math.random() * 40}%`} />
          </Cell>
        ))}
        <Cell>
          <SkeletonLine h="26px" w="80px" r="8px" />
        </Cell>
      </SkRow>
    ))}
  </>
);

// ── Stat cards skeleton ──────────────────────────────────────────────────────

const StatSkWrap = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: 20px 22px;
  box-shadow: ${p => p.theme.shadows.card};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

export const SkeletonStatCard = () => (
  <StatSkWrap>
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <SkeletonLine h="11px" w="55%" />
      <SkeletonLine h="28px" w="70%" />
      <SkeletonLine h="11px" w="45%" />
    </div>
    <SkeletonBlock h="52px" w="52px" r="14px" />
  </StatSkWrap>
);

// ── Alert card skeleton ──────────────────────────────────────────────────────

const AlertSkWrap = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  box-shadow: ${p => p.theme.shadows.card};
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const SkeletonAlertCard = () => (
  <AlertSkWrap>
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <SkeletonBlock h="40px" w="40px" r="10px" />
      <SkeletonLine h="24px" w="110px" r="6px" />
    </div>
    <SkeletonLine h="20px" w="65%" />
    <SkeletonLine h="26px" w="50%" />
    <SkeletonLine h="36px" w="100%" r="8px" />
    <SkeletonLine h="36px" w="100%" r="8px" />
  </AlertSkWrap>
);

// ── Empty State ──────────────────────────────────────────────────────────────

const ICONS = {
  clients:  MdPeopleOutline,
  receipts: MdReceiptLong,
  alerts:   MdNotificationsNone,
  reports:  MdBarChart,
  default:  MdInbox,
};

const EmptyWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
`;

const EmptyIconBox = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 20px;
  background: ${p => p.theme.colors.backgroundDark};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;

  svg {
    font-size: 2rem;
    color: ${p => p.theme.colors.textLight};
  }
`;

const EmptyTitle = styled.div`
  font-size: ${p => p.theme.fontSizes.lg};
  font-weight: 600;
  color: ${p => p.theme.colors.textPrimary};
  margin-bottom: 6px;
`;

const EmptySubtitle = styled.div`
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textLight};
  max-width: 320px;
  line-height: 1.6;
  margin-bottom: ${p => p.hasAction ? '24px' : '0'};
`;

const EmptyButton = styled.button`
  padding: 10px 24px;
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white;
  border: none;
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};
  box-shadow: 0 4px 14px ${p => p.theme.colors.primary}40;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px ${p => p.theme.colors.primary}50;
  }
`;

export const EmptyState = ({ type = 'default', title, subtitle, actionLabel, onAction }) => {
  const Icon = ICONS[type] || ICONS.default;
  return (
    <EmptyWrap>
      <EmptyIconBox>
        <Icon />
      </EmptyIconBox>
      <EmptyTitle>{title}</EmptyTitle>
      <EmptySubtitle hasAction={!!onAction}>{subtitle}</EmptySubtitle>
      {onAction && (
        <EmptyButton onClick={onAction}>{actionLabel}</EmptyButton>
      )}
    </EmptyWrap>
  );
};
